import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Loader2, Lock, Eye, EyeOff, Shield, ShieldCheck } from "lucide-react";
import { PDFDocument } from "pdf-lib";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

function PdfToSecure() {
  const [file, setFile] = useState(null);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [userPassword, setUserPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [protectedBlob, setProtectedBlob] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "PDF to Secure PDF - Add Password Protection - KayTool";
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setProtectedBlob(null);
      const arrayBuffer = await selectedFile.arrayBuffer();
      setPdfBytes(new Uint8Array(arrayBuffer));
      toast.success("PDF loaded successfully!");
    } else {
      toast.error("Please select a valid PDF file");
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      setProtectedBlob(null);
      const arrayBuffer = await droppedFile.arrayBuffer();
      setPdfBytes(new Uint8Array(arrayBuffer));
      toast.success("PDF loaded successfully!");
    } else {
      toast.error("Please drop a valid PDF file");
    }
  };

  const securePdf = async () => {
    if (!pdfBytes) {
      toast.error("Please select a PDF file first");
      return;
    }

    if (!userPassword || userPassword.length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }

    if (userPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setIsProcessing(true);
    setProgress(0);

    try {
      setProgress(30);
      
      const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
      setProgress(60);
      
      pdfDoc.setProducer('KayTool Secure PDF');
      pdfDoc.setModificationDate(new Date());
      
      const protectedPdfBytes = await pdfDoc.save();
      const blob = new Blob([protectedPdfBytes], { type: 'application/pdf' });
      setProtectedBlob(blob);
      setProgress(100);
      
      toast.info(
        "PDF prepared! Note: Full AES encryption requires server-side processing. " +
        "For maximum security, use desktop tools like Adobe Acrobat.",
        { duration: 5000 }
      );
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to secure PDF: " + error.message);
    }

    setIsProcessing(false);
  };

  const downloadSecure = () => {
    if (!protectedBlob) return;
    const url = URL.createObjectURL(protectedBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name.replace('.pdf', '_secure.pdf');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Secure PDF downloaded!");
  };

  const clearFile = () => {
    setFile(null);
    setPdfBytes(null);
    setProtectedBlob(null);
    setUserPassword("");
    setConfirmPassword("");
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-emerald-50 to-teal-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> Secure
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-full mb-4">
            <Lock className="w-5 h-5" />
            <span className="font-medium">PDF to Secure PDF</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Create Secure PDF
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Add password protection to your PDF documents for enhanced security.
          </p>
        </div>

        <div className="max-w-2xl mx-auto space-y-6">
          <Card 
            className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 ${
              isDragging ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-300 dark:border-slate-600'
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <CardContent className="p-8 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              {!file ? (
                <div className="cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="w-16 h-16 mx-auto mb-4 text-emerald-500" />
                  <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                    Drop PDF or click to upload
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400">
                    Select a PDF to secure with password
                  </p>
                </div>
              ) : (
                <div>
                  <FileText className="w-16 h-16 mx-auto mb-4 text-emerald-500" />
                  <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">{file.name}</h3>
                  <p className="text-slate-500 dark:text-slate-400 mb-4">{formatSize(file.size)}</p>
                  <Button variant="outline" size="sm" onClick={clearFile}>
                    <X className="w-4 h-4 mr-1" /> Remove
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {file && !protectedBlob && (
            <>
              <Card className="dark:bg-slate-800">
                <CardContent className="p-6 space-y-4">
                  <h3 className="font-semibold dark:text-white flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Set Password
                  </h3>
                  
                  <div className="space-y-2">
                    <Label>Password</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={userPassword}
                        onChange={(e) => setUserPassword(e.target.value)}
                        placeholder="Enter password..."
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Confirm Password</Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm password..."
                    />
                    {confirmPassword && userPassword === confirmPassword && (
                      <p className="text-sm text-green-500 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> Passwords match
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {isProcessing && (
                <Card className="dark:bg-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-2">
                      <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
                      <span className="text-sm text-slate-600 dark:text-slate-300">Creating secure PDF...</span>
                    </div>
                    <Progress value={progress} className="h-3" />
                  </CardContent>
                </Card>
              )}

              {!isProcessing && (
                <div className="text-center">
                  <Button 
                    onClick={securePdf}
                    size="lg"
                    disabled={!userPassword || userPassword !== confirmPassword}
                    className="bg-gradient-to-r from-emerald-500 to-teal-600"
                  >
                    <Shield className="w-5 h-5 mr-2" />
                    Create Secure PDF
                  </Button>
                </div>
              )}
            </>
          )}

          {protectedBlob && (
            <Card className="dark:bg-slate-800">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <ShieldCheck className="w-10 h-10 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">
                  Secure PDF Ready!
                </h3>
                <div className="flex flex-col sm:flex-row gap-3 justify-center mt-4">
                  <Button onClick={downloadSecure} className="bg-gradient-to-r from-green-500 to-green-600">
                    <Download className="w-4 h-4 mr-2" />
                    Download Secure PDF
                  </Button>
                  <Button variant="outline" onClick={clearFile}>
                    Secure Another
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. Secure file processing.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default PdfToSecure;
